/**
 * Shared contracts for the AI-agentic-testing layer (dev environment only).
 *
 * Auth:  `createAiAgentLoginToken` mints a Firebase custom token for allowlisted
 *        AI agent accounts — DEV project only, guarded server-side.
 * Game:  `AgentGameCommand` / `AgentGameCommandResult` / `AgentGameStateSnapshot`
 *        is the JSON protocol between `window.__agentGameApi` (web) and the Unity
 *        `AgentBridge` (SendMessage in, jslib callbacks out). The enums below are the
 *        COMPLETE vocabulary — every kind, item, path, known place and debug action an
 *        agent can reference. High-level typed helpers (collectUridium, goTo, …) live in
 *        `transition-web-evolve/src/agentTesting/agentGameApi.ts` on top of this.
 * Voice: no backend — agents synthesize audio locally (macOS `say`, Windows SAPI, espeak)
 *        and play it through `window.__agentVoiceApi.playAudioBase64(...)`.
 */

export type CreateAiAgentLoginTokenRequest = {
  email: string
}

export type CreateAiAgentLoginTokenResponse = {
  success: boolean
  token: string
  userId: string
}

// ---------------------------------------------------------------------------
// Game vocabulary — mirrors transition-game enums/scene naming. Source of truth
// noted per enum; if the game changes, update here in the same PR.
// ---------------------------------------------------------------------------

/** Interactable classes (transition-game Assets/Scripts/Gameplay/Interactables/). */
export enum AgentGameLocationKind {
  DOOR = 'Door',
  DEPOSIT = 'Deposit',
  ENTANGLEMENT_STATION = 'EntanglementStation',
  ROBOT = 'Robot',
  DRONE_PUZZLE = 'DronePuzzle',
  PICK_UP_ITEM = 'PickUpItem',
  HEATER = 'Heater',
  STIMPACK = 'Stimpack',
  CONTAINER = 'Container',
  ACTIVATION_OBJECT = 'ActivationObject',
  ACTIVATABLE_ITEM = 'ActivatableItem',
  IN_WORLD_BUTTON = 'InWorldButton'
}

/** Collectable item types (transition-game `ItemType`, Inventory.cs). */
export enum AgentGameItem {
  URIDIUM = 'Uridium',
  OXYGEN = 'Oxygen',
  HEATER = 'Heater',
  STIMPACK = 'Stimpack'
}

/** Map regions / difficulty paths (transition-game `GamePath`, PathBoundBox.cs). */
export enum AgentGamePath {
  BASE = 'Base',
  EASY = 'Easy',
  MEDIUM = 'Medium',
  HARD = 'Hard'
}

/** Game phases (transition-game `GameState`, GameStateMachine.cs). */
export enum AgentGamePhase {
  SETUP = 'Setup',
  ONBOARDING = 'Onboarding',
  REFLECTION = 'Reflection',
  PREPLANNING = 'Preplanning',
  IN_GAME = 'InGame',
  HELICOPTER_IN = 'HelicopterIn',
  HELICOPTER_OUT = 'HelicopterOut',
  TUTORIAL = 'Tutorial',
  RESTART_LOOP = 'RestartLoop'
}

/** Debug/state-manipulation actions (transition-game `DebugShortcuts.DebugAction`). */
export enum AgentGameDebugAction {
  REPAIR_DRONE = 'RepairDrone',
  COLLECT_ALL_DRONE_PIECES = 'CollectAllDronePieces',
  RESET_ALL_PUZZLE_PIECES = 'ResetAllPuzzlePieces',
  POWER_UP_STATION = 'PowerUpStation',
  END_SESSION = 'EndSession',
  RESTORE_PLAYER_STATUS = 'RestorePlayerStatus',
  LOOSE_ALL_HEALTH = 'LooseAllHealth',
  GAIN_5_HEATERS = 'Gain5Heaters',
  GAIN_5_MEDKITS = 'Gain5Medkits',
  RESET_SCIENTISTS = 'ResetScientists',
  ENABLE_ENTANGLEMENT = 'EnableEntanglement',
  UNLOCK_ROBOT = 'UnlockRobot',
  SKIP_ONBOARDING = 'SkipOnboarding',
  SHOW_DATABASE_INFO = 'ShowDatabaseInfo',
  POSSESS_AVATAR = 'PossessAvatar',
  RESET_ONBOARDING_MESSAGES = 'ResetOnboardingMessages',
  DISCONNECT_PLAYER = 'DisconnectPlayer',
  ADD_URIDIUM = 'AddUridium'
}

/** Debug action target (transition-game `DebugShortcuts.DebugPlayerSelect`). */
export enum AgentGameDebugTarget {
  ALL_PLAYERS = 'AllPlayers',
  PLAYER_1 = 'Player1',
  PLAYER_2 = 'Player2',
  PLAYER_3 = 'Player3',
  PLAYER_4 = 'Player4'
}

/** Preplanning ability choice (transition-game `AbilityType`; must be unique per player). */
export enum AgentGameAbility {
  SPEED = 'Speed',
  WARMTH = 'Warmth',
  HEAL = 'Heal',
  STRENGTH = 'Strength'
}

/** Reflection sub-phase (transition-game `ReflectionState.ReflectionStatePhase`). */
export enum AgentGameReflectionPhase {
  UNINITIALIZED = 'Uninitialized',
  PRE_CIRCUMPLEX = 'PreCircumplexPhase',
  REFLECTION = 'ReflectionPhase',
  POST_CIRCUMPLEX = 'PostCircumplexPhase',
  POST_REFLECTION = 'PostReflectionPhase',
  FINAL_GROUP_REFLECTION = 'FinalGroupReflectionPhase'
}

/** Reflection mode (transition-game `ReflectionMode`). */
export enum AgentGameReflectionMode {
  NOT_SET = 'NotSet',
  GROUP = 'Group',
  INDIVIDUAL = 'Individual'
}

/** Drone puzzle interactions (transition-game `DronePuzzle.DronePuzzleInteractions`). */
export enum AgentGameDronePuzzleInteraction {
  GRAB_FOCUS = 'GrabFocus',
  FLIP_HORIZONTAL = 'FlipHorizontal',
  FLIP_VERTICAL = 'FlipVertical',
  NEXT_LAYER = 'NextLayer',
  PREVIOUS_LAYER = 'PreviousLayer',
  RELEASE_FOCUS = 'ReleaseFocus'
}

/**
 * Semantic, ready-to-use destinations — THE answer to "go to… where?".
 * Resolved at runtime against live `listLocations` data via the matcher table below.
 */
export enum AgentGameKnownLocation {
  /** The uridium drop-off at the base (the "tower"). */
  URIDIUM_DEPOSIT = 'uridium-deposit',
  /** Nearest uridium chunk on the EASY path. */
  URIDIUM_EASY = 'uridium-easy',
  /** Nearest uridium chunk on the MEDIUM path. */
  URIDIUM_MEDIUM = 'uridium-medium',
  /** Nearest uridium chunk on the HARD path. */
  URIDIUM_HARD = 'uridium-hard',
  /** Nearest uridium chunk anywhere. */
  URIDIUM_NEAREST = 'uridium-nearest',
  /** Nearest oxygen pickup. */
  OXYGEN = 'oxygen',
  /** Nearest heater pickup. */
  HEATER = 'heater',
  /** Nearest stimpack (medkit) pickup. */
  STIMPACK = 'stimpack',
  ENTANGLEMENT_STATION = 'entanglement-station',
  ROBOT = 'robot',
  SLEEPING_PODS = 'sleeping-pods',
  /** Nearest door. */
  DOOR = 'door',
  /** Nearest storage container. */
  CONTAINER = 'container'
}

export type AgentKnownLocationMatcher = {
  /** Case-insensitive substrings matched against scene object names. Any hit qualifies. */
  namePatterns?: string[]
  /** Restrict to a specific interactable kind. */
  kind?: AgentGameLocationKind
  /** Restrict to pickups of this item type. */
  item?: AgentGameItem
  /** Restrict to a map path/difficulty region. */
  path?: AgentGamePath
  /** Human description shown in docs/errors. */
  description: string
}

/**
 * Matcher table for every known location. Scene names verified against
 * transition-game Assets/Scenes/GameScene.unity + Assets/Prefabs (spawned pickups are
 * named after their prefab, e.g. `Uridium(Clone)`).
 */
export const AGENT_KNOWN_LOCATIONS: Record<AgentGameKnownLocation, AgentKnownLocationMatcher> = {
  [AgentGameKnownLocation.URIDIUM_DEPOSIT]: {
    kind: AgentGameLocationKind.DEPOSIT,
    namePatterns: ['UridiumDeposit', 'Uridium Deposit'],
    description: 'Uridium drop-off at the base tower'
  },
  [AgentGameKnownLocation.URIDIUM_EASY]: {
    item: AgentGameItem.URIDIUM,
    path: AgentGamePath.EASY,
    description: 'Nearest uridium chunk on the easy path'
  },
  [AgentGameKnownLocation.URIDIUM_MEDIUM]: {
    item: AgentGameItem.URIDIUM,
    path: AgentGamePath.MEDIUM,
    description: 'Nearest uridium chunk on the medium path'
  },
  [AgentGameKnownLocation.URIDIUM_HARD]: {
    item: AgentGameItem.URIDIUM,
    path: AgentGamePath.HARD,
    description: 'Nearest uridium chunk on the hard path'
  },
  [AgentGameKnownLocation.URIDIUM_NEAREST]: {
    item: AgentGameItem.URIDIUM,
    description: 'Nearest uridium chunk anywhere'
  },
  [AgentGameKnownLocation.OXYGEN]: {
    item: AgentGameItem.OXYGEN,
    description: 'Nearest oxygen pickup'
  },
  [AgentGameKnownLocation.HEATER]: {
    item: AgentGameItem.HEATER,
    description: 'Nearest heater pickup'
  },
  [AgentGameKnownLocation.STIMPACK]: {
    item: AgentGameItem.STIMPACK,
    description: 'Nearest stimpack pickup'
  },
  [AgentGameKnownLocation.ENTANGLEMENT_STATION]: {
    kind: AgentGameLocationKind.ENTANGLEMENT_STATION,
    namePatterns: ['EntanglementStation'],
    description: 'Entanglement station'
  },
  // Note: no drone-puzzle known-location. DronePuzzle is a NetworkBehaviour (not an
  // Interactable), so it never appears in listLocations; the `dronePuzzle` command
  // operates on it globally and needs no navigation.
  [AgentGameKnownLocation.ROBOT]: {
    kind: AgentGameLocationKind.ROBOT,
    namePatterns: ['Robot'],
    description: 'Nearest robot'
  },
  [AgentGameKnownLocation.SLEEPING_PODS]: {
    namePatterns: ['SleepingPod'],
    description: 'Sleeping pods'
  },
  [AgentGameKnownLocation.DOOR]: {
    kind: AgentGameLocationKind.DOOR,
    namePatterns: ['Door'],
    description: 'Nearest door'
  },
  [AgentGameKnownLocation.CONTAINER]: {
    kind: AgentGameLocationKind.CONTAINER,
    namePatterns: ['Container'],
    description: 'Nearest storage container'
  }
}

// ---------------------------------------------------------------------------
// Wire protocol (web <-> Unity AgentBridge)
// ---------------------------------------------------------------------------

/** Low-level actions understood by the Unity AgentBridge. */
export type AgentGameAction =
  // world / navigation
  | 'ping'
  | 'getState'
  | 'listLocations'
  | 'moveTo'
  | 'goToNamed'
  | 'interactWith'
  | 'stop'
  // the elevator: a zone, walked into; on a dev build the host may leave once everyone is on board
  | 'goToElevator'
  | 'leaveThroughElevator'
  | 'debug'
  // reflection phase
  | 'reflectionReady'
  | 'reflectionAnswer'
  | 'circumplexSubmit'
  | 'reflectionNextLoop'
  | 'reflectionFinal'
  | 'reflectionQuit'
  // preplanning phase
  | 'preplanSelectAbility'
  | 'preplanToggleReady'
  // in-game HUD
  | 'dropItem'
  | 'useStimpack'
  | 'useHeater'
  | 'useAbility'
  | 'callDrone'
  | 'toggleCircumplex'
  | 'abortGame'
  // onboarding (spawn outside the base -> walk to the exit)
  | 'goToOnboardingExit'
  // robot unlock (field player reads the code, map player presses it)
  | 'robotInfo'
  | 'robotCodePress'
  | 'mapSelectRobot'
  // drone puzzle / map / lobby / dialogs
  | 'dronePuzzle'
  | 'openMap'
  | 'closeMap'
  | 'placeFlag'
  | 'lobbyStart'
  | 'lobbyLeave'
  | 'lobbyConfirmAudio'
  | 'confirmDialog'

export type AgentGameCommand = {
  /** Caller-generated id, echoed back in the matching AgentGameCommandResult. */
  id: string
  action: AgentGameAction
  /** Action-specific payload, see ai-agentic-testing/game-api.md. */
  params?: Record<string, unknown>
}

export type AgentGameCommandStatus = 'done' | 'failed'

export type AgentGameCommandResult = {
  id: string
  status: AgentGameCommandStatus
  error?: string
  /** Action-specific payload (e.g. locations list for `listLocations`). */
  data?: unknown
}

export type AgentGameLocation = {
  /** Stable-within-session id (`name#instanceId`) — pass to interactWith/goToNamed. */
  id: string
  name: string
  x: number
  y: number
  z: number
  /** Interactable class name — see AgentGameLocationKind. */
  kind: AgentGameLocationKind | string
  /** For PickUpItem locations: which item it is. */
  item?: AgentGameItem | string
  /** Which map region the location sits in. */
  path?: AgentGamePath | string
  /** For robots: Harvesting (ready to start), AwaitingConfirmation, Open or Locked. */
  state?: AgentGameRobotState | string
  distance?: number
}

export type AgentGameInventorySlot = {
  item: AgentGameItem | string
  amount: number
}

/** Robot lifecycle (transition-game `Robot.RobotState`; `Harvesting` is the default/untouched state). */
export enum AgentGameRobotState {
  HARVESTING = 'Harvesting',
  AWAITING_CONFIRMATION = 'AwaitingConfirmation',
  OPEN = 'Open',
  LOCKED = 'Locked'
}

/** Response of the `robotInfo` command. */
export type AgentGameRobotInfo = {
  id: string
  name: string
  state: AgentGameRobotState | string
  /** While AwaitingConfirmation: the CORRECT unlock code shown on the robot in the field. */
  displayedText: string | null
  /** The 5 candidate codes shown on the map, in button order (index = robotCodePress index). */
  candidateCodes: string[]
  distance?: number
}

/** Present on state snapshots only while the game is in the Onboarding phase. */
export type AgentGameOnboardingInfo = {
  exitX: number
  exitY: number
  exitZ: number
  requiredDistance: number
  distanceToExit: number
  playersReady: number
}

/** Present on state snapshots only while a loop runs (InGame). */
export type AgentGameElevatorInfo = {
  /** Metres from the player to the elevator's centre; within `range` counts as on board. */
  distance: number
  range: number
  playersOnBoard: number
  players: number
  /** Host only, dev builds: everyone is on board and `leaveThroughElevator` ends the loop now. */
  canLeave: boolean
}

/** Present on state snapshots only while the game is in the Reflection phase. */
export type AgentGameReflectionInfo = {
  phase: AgentGameReflectionPhase | string
  mode: AgentGameReflectionMode | string
  questionIndex: number
  questionCount: number
  /** Individual mode: the question on screen and how many there are (`questionIndex` stays put there). */
  individualQuestion?: number
  individualQuestionCount?: number
  readyPlayers: number
  players: number
  remainingTime: number
  /** Host-only actions (reflectionNextLoop/Final/Quit, lobbyStart) require this. */
  isHost: boolean
}

export type AgentGameStateSnapshot = {
  phase: AgentGamePhase | string
  playerX: number
  playerY: number
  playerZ: number
  isMoving: boolean
  /** 0..1 */
  health?: number
  /** 0..1 */
  oxygen?: number
  /** Which map region the player is in. */
  path?: AgentGamePath | string
  /** Seconds left in the running loop (0 outside InGame) — be on the elevator before it ends. */
  loopTimeLeft?: number
  inventory?: AgentGameInventorySlot[]
  /** Only set during the Reflection game phase. */
  reflection?: AgentGameReflectionInfo | null
  /** Only set during the Onboarding game phase. */
  onboarding?: AgentGameOnboardingInfo | null
  /** Only set while a loop runs. */
  elevator?: AgentGameElevatorInfo | null
  nearbyInteractables?: AgentGameLocation[]
  timestamp: number
}
